import type { TFunction } from "i18next";
import type { Journey, JourneyTask } from "../../types/journey";
import type {
	StepStatePayload,
	TaskProgressPayload,
} from "../journeyProgress/schemas";
import { GET_READY_TASK_CATALOG } from "./catalog";
import type { GetReadyTaskRenderModel } from "./types";
import { isGetReadyTaskKey } from "./types";

/**
 * Pure view-model adapter that converts the useJourneyProgress output
 * (journey + per-task progress) into the step-section shape GetReadyPage
 * renders. Returns every step in journey.steps sorted by step.order — the
 * caller chooses which sections to surface (S06 collapses to current step
 * via defaultOpen; S07 will further narrow to sub-steps within the current
 * step).
 *
 * Status derivation precedence: completedTaskIds (set) → taskProgress.status →
 * "pending" fallback. The set wins because optimistic completes patch the set
 * eagerly while taskProgress lags one tick behind in the hook's MergedPayload.
 *
 * Copy resolution: when taskKey is a known FE catalog entry, use catalog
 * i18n keys (so Get Ready strings stay localized centrally). Otherwise treat
 * the CDN-authored JourneyTask.title/description as authoritative — the CDN
 * JSON is already locale-fetched upstream.
 *
 * Pure: no React, no I/O, no Sentry. Mapping errors surface in the caller via
 * the existing mapping.ts pathway.
 */

export type StepSectionStatus = "in_progress" | "complete" | "not_started";

export interface JourneyProgressStepSection {
	stepKey: string;
	stepName: string;
	stepStatus: StepSectionStatus;
	order: number;
	tasks: GetReadyTaskRenderModel[];
}

const ACTION_CATEGORY_ICON_FALLBACKS: Record<string, string> = {
	fill: "fill",
	upload: "upload",
	review_sign: "review-sign",
	book: "book",
	test: "test",
	notify: "notify",
	monitor: "monitor",
	decide: "book",
	research: "book",
	pay_finance: "book",
	connect: "book",
};

function resolveIconName(task: JourneyTask): string {
	if (typeof task.iconName === "string" && task.iconName.length > 0) {
		return task.iconName;
	}
	const ac = task.actionCategory;
	if (typeof ac === "string" && ac in ACTION_CATEGORY_ICON_FALLBACKS) {
		return ACTION_CATEGORY_ICON_FALLBACKS[ac];
	}
	return "book";
}

function deriveRenderStatus(
	taskId: string,
	completedTaskIds: ReadonlySet<string>,
	taskProgress: ReadonlyMap<string, TaskProgressPayload>,
): GetReadyTaskRenderModel["status"] {
	if (completedTaskIds.has(taskId)) return "completed";
	const tp = taskProgress.get(taskId);
	if (!tp) return "pending";
	if (tp.status === "complete") return "completed";
	if (tp.status === "in_progress") return "in_progress";
	return "pending";
}

function mapTaskToRenderModel(
	task: JourneyTask,
	indexWithinStep: number,
	status: GetReadyTaskRenderModel["status"],
	t: TFunction,
	progressPercent: number | null,
): GetReadyTaskRenderModel {
	const taskKey = task.id;

	let title: string;
	let subtext: string | undefined;
	let iconName: string;

	if (isGetReadyTaskKey(taskKey)) {
		const entry = GET_READY_TASK_CATALOG[taskKey];
		title = t(entry.titleKey);
		subtext = t(entry.subtextKey);
		iconName = entry.iconName;
	} else {
		title = task.title;
		subtext = task.sevenWordIntro || task.description || undefined;
		iconName = resolveIconName(task);
	}

	const progressTotal =
		typeof task.progressTotal === "number" ? task.progressTotal : undefined;
	const pct = progressPercent ?? 0;
	const progress: GetReadyTaskRenderModel["progress"] =
		progressTotal !== undefined
			? {
					current: Math.round((pct / 100) * progressTotal),
					total: progressTotal,
					unit: task.progressUnit ?? "items",
					percent: pct,
				}
			: undefined;

	return {
		id: task.id,
		kind: "catalog",
		taskKey,
		status,
		order: indexWithinStep,
		deadlineText: task.dueDateLabel || task.deadline || undefined,
		title,
		subtext,
		// Carry the full CDN "about" + tips so the Get Ready detail sheet shows
		// the same rich info as Journey (86ey1tzqx). The CDN payload isn't
		// per-field validated, so coerce: only forward a non-empty string
		// description / an array of string tips (else the sheet could render a
		// non-string and crash).
		description:
			typeof task.description === "string" && task.description
				? task.description
				: undefined,
		prepTips: Array.isArray(task.prepTips)
			? task.prepTips.filter((x): x is string => typeof x === "string")
			: undefined,
		iconName,
		taskDomain: task.taskDomain,
		actionCategory: task.actionCategory,
		progress,
	};
}

export interface AdaptJourneyProgressInput {
	journey: Journey | null;
	stepStates: ReadonlyMap<string, StepStatePayload>;
	taskProgress: ReadonlyMap<string, TaskProgressPayload>;
	completedTaskIds: ReadonlySet<string>;
	t: TFunction;
}

function resolveStepStatus(
	stepId: string,
	stepStates: ReadonlyMap<string, StepStatePayload>,
): StepSectionStatus {
	const state = stepStates.get(stepId);
	if (!state) return "not_started";
	if (state.status === "in_progress") return "in_progress";
	if (state.status === "complete") return "complete";
	return "not_started";
}

// ─── Sub-step adapter (T07) ───────────────────────────────────────────────────

/**
 * Groups the tasks of the first in_progress step into sub-step sections.
 * Algorithm:
 *  1. Find the first step whose stepState.status === "in_progress".
 *  2. If none, return [].
 *  3. Iterate that step's tasks in CDN order.
 *  4. Group by task.subStepId ?? '__ungrouped__', carrying the first-encountered
 *     subStepTitle.
 *  5. Per group compute completedCount from completedTaskIds and totalCount from
 *     group length.
 *
 * Custom tasks (__custom__) are NOT produced here; GetReadyPage appends them
 * separately.
 */
export interface SubStepSection {
	subStepKey: string;
	subStepTitle: string;
	order: number;
	completedCount: number;
	totalCount: number;
	tasks: GetReadyTaskRenderModel[];
}

export function adaptJourneyProgressToSubStepSections(
	input: AdaptJourneyProgressInput,
): SubStepSection[] {
	const { journey, stepStates, taskProgress, completedTaskIds, t } = input;
	if (!journey) return [];

	const sortedSteps = journey.steps.slice().sort((a, b) => a.order - b.order);

	// (1) Find first in_progress step.
	const currentStep = sortedSteps.find((step) => {
		const state = stepStates.get(step.id);
		return state?.status === "in_progress";
	});

	// (2) No in_progress step → empty.
	if (!currentStep) return [];

	// (3)+(4) Iterate tasks in CDN order, group by subStepId.
	const groupOrder: string[] = [];
	const groupTitleMap = new Map<string, string>();
	const groupTasksMap = new Map<string, GetReadyTaskRenderModel[]>();

	currentStep.tasks.forEach((task, index) => {
		const groupKey = task.subStepId ?? "__ungrouped__";
		if (!groupTasksMap.has(groupKey)) {
			groupOrder.push(groupKey);
			// Carry the first-encountered subStepTitle (or fallback to the group key).
			groupTitleMap.set(
				groupKey,
				groupKey === "__ungrouped__" ? "" : (task.subStepTitle ?? groupKey),
			);
			groupTasksMap.set(groupKey, []);
		}
		const status = deriveRenderStatus(task.id, completedTaskIds, taskProgress);
		const tp = taskProgress.get(task.id);
		const renderModel = mapTaskToRenderModel(
			task,
			index,
			status,
			t,
			tp?.progressPercent ?? null,
		);
		// biome-ignore lint/style/noNonNullAssertion: key was just inserted above
		groupTasksMap.get(groupKey)!.push(renderModel);
	});

	// (5) Build SubStepSection array preserving insertion order.
	return groupOrder.map((groupKey, sectionIndex) => {
		// biome-ignore lint/style/noNonNullAssertion: key always present
		const tasks = groupTasksMap.get(groupKey)!;
		const completedCount = tasks.filter((task) =>
			completedTaskIds.has(task.id),
		).length;
		return {
			subStepKey: groupKey,
			subStepTitle: groupTitleMap.get(groupKey) ?? "",
			order: sectionIndex,
			completedCount,
			totalCount: tasks.length,
			tasks,
		};
	});
}

// ─── Step-level adapter (legacy, kept for reference) ─────────────────────────

/**
 * @deprecated Use adaptJourneyProgressToSubStepSections for the Get Ready page.
 * This function groups by step and is retained until all callers migrate.
 */
export function adaptJourneyProgressToStepSections(
	input: AdaptJourneyProgressInput,
): JourneyProgressStepSection[] {
	const { journey, stepStates, taskProgress, completedTaskIds, t } = input;
	if (!journey) return [];

	const sortedSteps = journey.steps.slice().sort((a, b) => a.order - b.order);

	return sortedSteps.map((step) => {
		const tasks = step.tasks.map((task, index) => {
			const status = deriveRenderStatus(
				task.id,
				completedTaskIds,
				taskProgress,
			);
			const tp = taskProgress.get(task.id);
			return mapTaskToRenderModel(
				task,
				index,
				status,
				t,
				tp?.progressPercent ?? null,
			);
		});

		return {
			stepKey: step.id,
			stepName: step.title,
			stepStatus: resolveStepStatus(step.id, stepStates),
			order: step.order,
			tasks,
		};
	});
}
